import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Container from 'common/Container';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { WikiHero } from '../WikiOperadorAudiovisual/components';
import { fetchKnowledgePagesByCategory } from 'services/academyKnowledgeApi';
import {
  WIKI_PUBLICAR_NOTICIAS_TNO_CATEGORY,
  WIKI_PUBLICAR_NOTICIAS_TNO_HERO_SLUG,
  WIKI_PUBLICAR_NOTICIAS_TNO_BODY_SLUG,
} from 'constants/wikiPublicarNoticiasTno';

const PAGE_PADDING_X = { xs: 0.25, sm: 0.5 };
const CONTAINER_PROPS = {
  paddingX: PAGE_PADDING_X,
  maxWidth: { xs: '100%', sm: '100%', md: 1320 },
};

function sortByOrder(pages) {
  return [...pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

const articleSx = {
  maxWidth: '100%',
  width: '100%',
  mx: 0,
  py: { xs: 3, md: 5 },
  '& h2': { mt: 4, mb: 1.5, fontSize: '1.35rem', fontWeight: 700 },
  '& h3': { mt: 2.5, mb: 1, fontSize: '1.1rem', fontWeight: 600 },
  '& p': { mb: 1.5, lineHeight: 1.7 },
  '& ul, & ol': { pl: 2.5, mb: 2 },
  '& li': { mb: 0.75 },
  '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 },
  '& strong': { fontWeight: 600 },
  '& .wiki-ghost-flow': {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    flexWrap: 'wrap',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: { xs: 0.5, md: 1 },
    my: 3,
    py: 2.5,
    px: { xs: 1.5, sm: 2 },
    borderRadius: 2,
    bgcolor: 'alternate.main',
    borderLeft: '4px solid',
    borderColor: 'primary.main',
  },
  '& .wiki-ghost-flow__step': {
    flex: { xs: '0 0 auto', md: '1 1 120px' },
    width: { xs: '100%', md: 'auto' },
    maxWidth: { xs: 440, md: 220 },
    mx: { xs: 'auto', md: 0 },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    py: 1.5,
    px: 1.25,
    borderRadius: 1,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 1,
  },
  '& .wiki-ghost-flow__num': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    fontSize: '0.8125rem',
    fontWeight: 800,
    mb: 1,
  },
  '& .wiki-ghost-flow__step strong': {
    display: 'block',
    mb: 0.5,
    fontSize: '0.9375rem',
    color: 'text.primary',
  },
  '& .wiki-ghost-flow__txt': {
    display: 'block',
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    color: 'text.secondary',
  },
  '& .wiki-ghost-flow__arrow': {
    alignSelf: 'center',
    color: 'text.secondary',
    fontSize: '1.35rem',
    fontWeight: 300,
    lineHeight: 1,
    userSelect: 'none',
    flexShrink: 0,
  },
  '& .wiki-ghost-flow__arrow--down': {
    display: { xs: 'block', md: 'none' },
    width: '100%',
    textAlign: 'center',
    py: 0.15,
  },
  '& .wiki-ghost-flow__arrow--right': {
    display: { xs: 'none', md: 'block' },
    px: 0.15,
  },
};

const LandingWikiPublicarNoticiasTno = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchKnowledgePagesByCategory(WIKI_PUBLICAR_NOTICIAS_TNO_CATEGORY);
        if (!cancelled) setPages(Array.isArray(data) ? sortByOrder(data) : []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar el manual.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { hero, bodyPage } = useMemo(() => {
    const sorted = sortByOrder(pages);
    const heroPage =
      sorted.find((p) => p.slug === WIKI_PUBLICAR_NOTICIAS_TNO_HERO_SLUG) || sorted[0] || null;
    const body = sorted.find((p) => p.slug === WIKI_PUBLICAR_NOTICIAS_TNO_BODY_SLUG) || null;
    return { hero: heroPage, bodyPage: body };
  }, [pages]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Container {...CONTAINER_PROPS}>
        <Box py={8}>
          <Typography variant="h5" gutterBottom>
            No se pudo cargar el manual
          </Typography>
          <Typography color="textSecondary" paragraph>
            {error}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Categoría Academy: «{WIKI_PUBLICAR_NOTICIAS_TNO_CATEGORY}». Ejecuta{' '}
            <code>npm run seed:wiki-publicar-noticias-tno</code> en <code>tnoradio-academy-service</code>.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!hero || !bodyPage) {
    return (
      <Container {...CONTAINER_PROPS}>
        <Box py={8}>
          <Typography variant="h5" gutterBottom>
            Manual incompleto o sin publicar
          </Typography>
          <Typography color="textSecondary">
            Deben existir las entradas «{WIKI_PUBLICAR_NOTICIAS_TNO_HERO_SLUG}» y «
            {WIKI_PUBLICAR_NOTICIAS_TNO_BODY_SLUG}» en Academy. Vuelve a correr el seed.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      <Box minHeight={{ xs: 'auto', md: 'calc(100vh - 64px)' }} position="relative">
        <WikiHero
          overline="Ghost · Canva · Instagram"
          title={hero.title}
          description={hero.description || ''}
          contentHtml={hero.content || ''}
          containerProps={CONTAINER_PROPS}
          contentHtmlSx={{ maxWidth: '100%', mx: 0 }}
        />
      </Box>
      <Box bgcolor={theme.palette.alternate.main}>
        <Container {...CONTAINER_PROPS}>
          <Typography variant="h5" component="h1" fontWeight={800} sx={{ pt: 3, pb: 1 }}>
            {bodyPage.title}
          </Typography>
          {bodyPage.description ? (
            <Typography variant="body1" color="textSecondary" paragraph>
              {bodyPage.description}
            </Typography>
          ) : null}
        </Container>
      </Box>
      <Container {...CONTAINER_PROPS}>
        <Box component="article" sx={articleSx} dangerouslySetInnerHTML={{ __html: bodyPage.content || '' }} />
      </Container>
    </Box>
  );
};

LandingWikiPublicarNoticiasTno.propTypes = {
  themeMode: PropTypes.string,
};

export default LandingWikiPublicarNoticiasTno;
